<template>
  <div class="ck-catalogue">
    <!-- Recherche, catégories et sous-catégories -->
    <section class="ck-card ck-toolbar">
      <form v-on:submit.prevent class="ck-search">
        <input
          type="search"
          placeholder="Rechercher une prestation, un produit, un bon cadeau…"
          aria-label="Rechercher dans le catalogue"
          v-model="researchValue"
          @input="onSearch"
        />
        <span v-if="loading" class="ck-loading"></span>
      </form>
      <div class="ck-toolbar-row">
        <div class="ck-tabs" role="tablist" aria-label="Catégories du catalogue">
          <button
            v-for="category in categoryList"
            :key="category.name"
            type="button"
            role="tab"
            :class="{ 'is-active': scope === category.name }"
            :aria-selected="scope === category.name"
            @click="selectCategory(category.name)"
          >{{ category.name }}</button>
        </div>
        <select v-if="groups.length > 1" v-model="group" class="ck-filter" aria-label="Sous-catégorie">
          <option value="">Toutes les catégories</option>
          <option v-for="name in groups" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
    </section>

    <!-- Articles -->
    <section class="ck-catalogue-list">
      <div class="ck-grid" :aria-busy="loading">
        <!-- Squelettes pendant le chargement d'une catégorie ou d'une recherche -->
        <template v-if="loading && !items.length">
          <span v-for="n in perPage" :key="'sk' + n" class="ck-tile is-skeleton" aria-hidden="true">
            <span class="ck-tile-img"></span>
            <span class="ck-tile-body">
              <span class="ck-skel" style="width: 75%"></span>
              <span class="ck-skel" style="width: 100%"></span>
              <span class="ck-skel" style="width: 60%"></span>
              <span class="ck-tile-foot"><span class="ck-skel" style="width: 40%"></span><span class="ck-skel ck-skel-price"></span></span>
            </span>
          </span>
        </template>
        <!-- Bon cadeau d'un montant libre -->
        <button v-if="isVoucherList && page === 1" type="button" class="ck-tile" @click="openFixedAmount">
          <span class="ck-tile-img is-icon"><i class="bi bi-currency-euro" aria-hidden="true"></i></span>
          <span class="ck-tile-body">
            <strong>Montant libre</strong>
            <span class="ck-tile-desc">Bon cadeau d'un montant au choix, à saisir à l'ajout.</span>
            <span class="ck-tile-foot"><small>Bon cadeau</small><span class="ck-price">Au choix</span></span>
          </span>
        </button>

        <template v-for="value in pageItems">
          <!-- Article : toute la carte ajoute au panier (mêmes champs qu'avant) -->
          <form v-if="!isVoucherList" :key="value.cat + value.id" method="POST" :action="prestaAdd" @submit="onSubmit($event, value)">
            <input type="hidden" name="_token" :value="csrf" />
            <input type="hidden" name="type" :value="scope || value.cat" />
            <input type="hidden" name="item_id" :value="value.id" />
            <input type="hidden" name="price_ht" :value="value.discount_price > 0 ? (value.price_ht - value.discount_price) : value.price_ht" />
            <input v-if="live.orderId" type="hidden" name="order_id" :value="live.orderId" />
            <input v-if="live.userId" type="hidden" name="user_id" :value="live.userId" />
            <input type="hidden" name="priceModalPresta" :value="value.discount_price > 0 ? (value.price - value.discount_price) : value.price" />
            <input type="hidden" name="prestationLabel" :value="value.name" />
            <input type="hidden" name="quantity" value="1" />
            <input type="hidden" name="timeQty" :value="value.duration" />
            <input type="hidden" name="prestaStatus" value="1" />
            <button
              type="submit"
              class="ck-tile"
              :class="{ 'is-added': isInCart(value), 'is-pending': pendingKey === value.cat + value.id }"
              :title="isInCart(value) ? 'Déjà au panier : ajouter encore « ' + value.name + ' »' : 'Ajouter « ' + value.name + ' » au panier'"
            >
              <span class="ck-tile-img">
                <img v-if="value.img1 || value.image" :src="value.img1 || value.image" alt="" loading="lazy" @error="$event.target.remove()" />
                <span v-if="isInCart(value)" class="ck-tile-badge"><i class="bi bi-check2" aria-hidden="true"></i> Au panier</span>
              </span>
              <span class="ck-tile-body">
                <strong>{{ value.name }}</strong>
                <span v-if="value.description" class="ck-tile-desc">{{ value.description }}</span>
                <span class="ck-tile-foot">
                  <small>{{ meta(value) }}</small>
                  <span class="ck-prices">
                    <s v-if="value.discount_price > 0" class="ck-price is-old">{{ formatPrice(value.price) }}</s>
                    <span class="ck-price">{{ formatPrice(value.discount_price > 0 ? value.price - value.discount_price : value.price) }}</span>
                  </span>
                </span>
              </span>
            </button>
          </form>

          <!-- Modèle de bon cadeau -->
          <form v-else :key="'v' + value.cat + value.id" method="POST" :action="voucherAdd" @submit="onSubmit($event, value)">
            <input type="hidden" name="_token" :value="csrf" />
            <input type="hidden" name="type" :value="value.cat" />
            <input type="hidden" name="item_id" :value="value.id" />
            <input type="hidden" name="amount" :value="amount" />
            <input v-if="live.orderId" type="hidden" name="order_id" :value="live.orderId" />
            <input v-if="live.userId" type="hidden" name="user_id" :value="live.userId" />
            <input type="hidden" name="bonCadeau" :value="true" />
            <input type="hidden" name="priceModalPresta" :value="value.price" />
            <input type="hidden" name="prestationLabel" :value="value.name" />
            <input type="hidden" name="quantity" value="1" />
            <input type="hidden" name="timeQty" value="30" />
            <input type="hidden" name="prestaStatus" value="1" />
            <button type="submit" class="ck-tile" :class="{ 'is-pending': pendingKey === value.cat + value.id }" :title="'Ajouter un bon cadeau « ' + value.name + ' »'">
              <span class="ck-tile-img">
                <img v-if="value.img1 || value.image" :src="value.img1 || value.image" alt="" loading="lazy" @error="$event.target.remove()" />
              </span>
              <span class="ck-tile-body">
                <strong>{{ value.name }}</strong>
                <span v-if="value.description" class="ck-tile-desc">{{ value.description }}</span>
                <span class="ck-tile-foot">
                  <small>{{ meta(value) }}</small>
                  <span class="ck-prices"><span class="ck-price">{{ formatPrice(value.price) }}</span></span>
                </span>
              </span>
            </button>
          </form>
        </template>
      </div>

      <p v-if="!loading && !filtered.length && !isVoucherList" class="ck-empty">
        {{ researchValue ? 'Aucun article ne correspond à « ' + researchValue + ' ».' : 'Aucun article dans cette catégorie.' }}
      </p>

      <!-- Pagination -->
      <div v-if="filtered.length" class="ck-pager">
        <span>{{ rangeLabel }}</span>
        <nav aria-label="Pages du catalogue">
          <button type="button" :disabled="page === 1" @click="page--">Précédent</button>
          <template v-for="(p, index) in visiblePages">
            <span v-if="index && p - visiblePages[index - 1] > 1" :key="'gap-' + p">…</span>
            <button type="button" :key="p" :class="{ 'is-active': p === page }" @click="page = p">{{ p }}</button>
          </template>
          <button type="button" :disabled="page === pageCount" @click="page++">Suivant</button>
        </nav>
      </div>
    </section>

    <!-- Bon cadeau d'un montant libre -->
    <div v-if="fixedAmount" class="modal fade show catalogue-modal" style="display: block" tabindex="-1" role="dialog" aria-labelledby="ck-amount-title" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <form class="modal-content" :action="voucherAdd" method="POST" @submit="onSubmit($event, null)">
          <input type="hidden" name="_token" :value="csrf" />
          <input type="hidden" name="type" value="Bons cadeau" />
          <input v-if="live.orderId" type="hidden" name="order_id" :value="live.orderId" />
          <input v-if="live.userId" type="hidden" name="user_id" :value="live.userId" />
          <input type="hidden" name="prestationLabel" :value="'Bon cadeau de ' + amount + '€'" />
          <input type="hidden" name="priceModalPresta" :value="amount" />
          <div class="catalogue-modal-header">
            <div>
              <p class="catalogue-modal-eyebrow">Bon cadeau</p>
              <h2 class="catalogue-modal-title" id="ck-amount-title">Montant libre</h2>
            </div>
            <button type="button" class="catalogue-modal-close" aria-label="Fermer" @click="fixedAmount = false">×</button>
          </div>
          <div class="catalogue-modal-body">
            <div class="catalogue-modal-grid">
              <div>
                <label class="catalogue-modal-label" for="ck-amount">Montant (€) · requis</label>
                <input type="number" min="1" step="0.01" class="catalogue-modal-input" id="ck-amount" name="amount" v-model="amount" required />
              </div>
              <div>
                <label class="catalogue-modal-label" for="ck-amount-qty">Quantité</label>
                <input type="number" min="1" class="catalogue-modal-input" id="ck-amount-qty" name="quantity" value="1" />
              </div>
            </div>
          </div>
          <div class="catalogue-modal-footer">
            <button type="button" class="catalogue-modal-cancel" @click="fixedAmount = false">Annuler</button>
            <button type="submit" class="catalogue-modal-submit">Ajouter au panier</button>
          </div>
        </form>
      </div>
    </div>
    <div v-if="fixedAmount" class="modal-backdrop fade show"></div>
  </div>
</template>

<style>
/* Global : réduit les conteneurs Vuetify de la page (v-app ailleurs dans la caisse) et masque les flèches des champs numériques */
input[type="number"] {
  -moz-appearance: textfield;
}
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.v-application--wrap {
  flex: 1 1 auto;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  display: flex;
  flex-direction: column;
  min-height: 20vh;
  max-width: 100%;
  position: relative;
}
</style>

<script>
// Catalogue de la caisse : onglets de catégories, filtre de sous-catégorie, grille paginée de cartes.
// La recherche (nom ou code-barres, route checkoutResearch) porte sur tout le catalogue, sauf dans l'onglet Bons cadeau
// où elle reste sur les modèles de bons. Ajout au panier : formulaires prestaAdd / voucherAdd (toute la carte est le bouton).
const CATEGORY_META = {
  'Prestations': { icon: 'bi bi-person', unit: 'articles', type: 'Prestation' },
  'Produits': { icon: 'bi bi-box-seam', unit: 'articles', type: 'Product' },
  'Bons cadeau': { icon: 'bi bi-gift', unit: 'modèles', type: 'Voucher' },
  'Abonnements': { icon: 'bi bi-arrow-repeat', unit: 'formules', type: 'Serie' },
  'Cures': { icon: 'bi bi-heart', unit: 'cures', type: 'Treatment' },
  'Forfaits': { icon: 'bi bi-layers', unit: 'forfaits', type: 'Package' },
}
const CAT_TO_TYPE = { Prestations: 'Prestation', Produits: 'Product', Abonnements: 'Serie', Cure: 'Treatment', Cures: 'Treatment', Forfaits: 'Package' }

export default {
  name: 'checkout-component',
  props: [
    'prestaAdd',
    'voucherAdd',
    'categories',
    'counts',
    'inCart',
    'researchRoute',
    'currentCustomer',
    'orderId',
    'userId',
    // anciens paramètres, encore passés par la page
    'getPrestationsBySubcategory',
    'getPrestationsByVoucherSubcategory',
    'getProductsBySubcategory',
  ],
  data: () => ({
    csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
    activeCategory: null,
    items: [],
    researchValue: '',
    loading: false,
    timeout: null,
    lastRequest: 0,
    page: 1,
    perPage: 6,
    group: '',
    submitting: false,
    pendingKey: null,
    // État de la caisse : props au chargement, puis mis à jour sans rechargement (événement ck-state de la page)
    live: { orderId: 0, userId: 0, inCart: [] },
    fixedAmount: false,
    amount: 1,
  }),
  computed: {
    // Catégories présentes dans la caisse, dans l'ordre de la maquette
    categoryList () {
      const available = Object.keys(this.categories || {})
      return Object.keys(CATEGORY_META)
        .filter((name) => available.includes(name))
        .map((name) => ({ name, ...CATEGORY_META[name], count: (this.counts || {})[name] ?? 0 }))
    },
    searching () {
      return this.researchValue.trim() !== ''
    },
    // Catégorie interrogée : aucune pendant une recherche (tout le catalogue), sauf dans l'onglet Bons cadeau
    scope () {
      return this.searching && this.activeCategory !== 'Bons cadeau' ? null : this.activeCategory
    },
    isVoucherList () {
      return this.scope === 'Bons cadeau'
    },
    // Sous-catégories présentes dans la liste chargée
    groups () {
      return [...new Set(this.items.map((value) => value && value.group_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'))
    },
    filtered () {
      return this.items.filter((value) => value && value.id && (!this.group || value.group_name === this.group))
    },
    pageCount () {
      return Math.max(1, Math.ceil(this.filtered.length / this.perPage))
    },
    pageItems () {
      const start = (this.page - 1) * this.perPage
      return this.filtered.slice(start, start + this.perPage)
    },
    rangeLabel () {
      const total = this.filtered.length
      const start = (this.page - 1) * this.perPage + 1
      const end = Math.min(total, this.page * this.perPage)
      return `${start}–${end} sur ${total.toLocaleString('fr-FR')}`
    },
    // Première, dernière et pages voisines
    visiblePages () {
      const pages = new Set([1, this.pageCount])
      for (let i = this.page - 1; i <= this.page + 1; i++) {
        if (i >= 1 && i <= this.pageCount) pages.add(i)
      }
      return [...pages].sort((a, b) => a - b)
    },
  },
  watch: {
    group () {
      this.page = 1
    },
  },
  created () {
    this.live = { orderId: this.orderId || 0, userId: this.userId || 0, inCart: this.inCart || [] }
  },
  mounted () {
    window.addEventListener('ck-state', this.onState)
    window.addEventListener('ck-idle', this.onIdle)
    let saved = null
    try { saved = sessionStorage.getItem('checkoutCategory') } catch (e) {}
    const first = this.categoryList.find((category) => category.name === saved) || this.categoryList.find((category) => category.name === 'Prestations') || this.categoryList[0]
    if (first) this.selectCategory(first.name)
  },
  beforeDestroy () {
    window.removeEventListener('ck-state', this.onState)
    window.removeEventListener('ck-idle', this.onIdle)
  },
  methods: {
    selectCategory (name) {
      this.activeCategory = name
      this.researchValue = ''
      this.group = ''
      this.page = 1
      try { sessionStorage.setItem('checkoutCategory', name) } catch (e) {}
      this.load()
    },

    onSearch () {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.page = 1
        this.group = ''
        this.load()
      }, 300)
    },

    load () {
      const request = Date.now()
      this.lastRequest = request
      this.loading = true
      this.items = []
      axios
        .get(this.researchRoute, { params: { name: this.researchValue, category: this.scope } })
        .then((response) => {
          if (request !== this.lastRequest) return
          this.items = Array.isArray(response.data) ? response.data : Object.values(response.data || {})
        })
        .catch((error) => console.error(error))
        .finally(() => {
          if (request === this.lastRequest) this.loading = false
        })
    },

    // Un seul envoi à la fois (double-clic = un seul article) ; la page l'envoie sans rechargement
    onSubmit (event, value) {
      if (this.submitting) {
        event.preventDefault()
        return
      }
      this.submitting = true
      this.pendingKey = value ? value.cat + value.id : null
      this.fixedAmount = false
    },

    onState (event) {
      const state = event.detail || {}
      this.live = { orderId: state.orderId || 0, userId: state.userId || 0, inCart: state.inCart || [] }
    },

    onIdle () {
      this.submitting = false
      this.pendingKey = null
    },

    openFixedAmount () {
      this.amount = 1
      this.fixedAmount = true
    },

    isInCart (value) {
      return (this.live.inCart || []).includes(`${CAT_TO_TYPE[value.cat] || value.cat}:${value.id}`)
    },

    // Ligne secondaire : durée / stock / séances + sous-catégorie
    meta (value) {
      const parts = []
      if (value.cat === 'Prestations' && value.commercial_time) parts.push(`${value.commercial_time} min`)
      if (value.cat === 'Produits') parts.push(`Produit · stock ${value.quantity ?? 0}`)
      if (value.cat === 'Cure' && value.prestations_qty) parts.push(`${value.prestations_qty} séances`)
      if (value.cat === 'Abonnements') parts.push(value.prestations_qty > 100 ? 'Illimité' : `${value.prestations_qty || 0} prestations / période`)
      if (value.cat === 'Forfaits') parts.push('Forfait')
      if (this.isVoucherList) parts.push('Bon cadeau')
      if (value.group_name) parts.push(value.group_name)
      return parts.join(' · ')
    },

    formatPrice (value) {
      const number = Number(value || 0)
      return number.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: Number.isInteger(number) ? 0 : 2 })
    },
  },
}
</script>
